import React, { useEffect, useMemo, useState } from 'react';
import { BrioModal, BrioModalBody, BrioButton, BrioSelect } from '../../components/BrioComponents';
import { formatSeriesRange } from '../utils/itemCodeGenerator';
import { canonicalUnit } from '../utils/measureUnits';

// ============================================================================
// Split an item's versions into items of their own.
//
// Every live version is listed with a tick box, the first included. Each
// ticked version leaves with its own rates (rates follow the version id) and
// takes the code shown beside it. The earliest version left unticked keeps the
// item's code, so at least one has to stay. A version whose sub-trade
// has trade types but which carries none gets a trade type picker, because the
// trade type's series is what numbers its new code. The codes come from a dry
// run of boq.split_library_item, the same path as the real split rolled back,
// so what is shown is what will be written. Nothing changes until Split.
//
// Nothing is ticked to start with. Archived versions are not listed
// (boq.split_candidates returns live versions only).
// ============================================================================

const cell = { padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--brio-border-subtle, #eee)', verticalAlign: 'top' };
const muted = { color: 'var(--brio-text-muted)', fontSize: '0.76rem' };

function SplitItemModal({ itemCode, tradeTypes, loadCandidates, onSplit, onClose }) {
  const [versions, setVersions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [typeFor, setTypeFor] = useState({});     // versionId → trade type id chosen here
  const [preview, setPreview] = useState(null);   // versionId → { newCode, problem }
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    loadCandidates(itemCode).then((res) => {
      if (!live) return;
      if (!res.success) { setLoadError(res.error); return; }
      setVersions(res.versions);
      setPicked(new Set());
    });
    return () => { live = false; };
  }, [itemCode, loadCandidates]);

  const typesByAnchor = useMemo(() => {
    const m = new Map();
    (tradeTypes || []).forEach((t) => {
      if (!t.anchorId) return;
      if (!m.has(t.anchorId)) m.set(t.anchorId, []);
      m.get(t.anchorId).push(t);
    });
    return m;
  }, [tradeTypes]);

  const plan = useMemo(() => (versions || [])
    .filter((v) => picked.has(v.id))
    .map((v) => ({ versionId: v.id, tradeTypeId: typeFor[v.id] || null })), [versions, picked, typeFor]);

  // Re-run the dry run whenever the plan changes. A slow answer for an older
  // plan is dropped so it cannot overwrite the current one.
  useEffect(() => {
    if (!versions || plan.length === 0) { setPreview(null); return undefined; }
    let live = true;
    setPreviewing(true);
    const t = setTimeout(async () => {
      const res = await onSplit(itemCode, plan, { dryRun: true });
      if (!live) return;
      setPreviewing(false);
      if (!res.success) { setError(res.error); setPreview(null); return; }
      setError(null);
      setPreview(new Map(res.rows.map((r) => [r.versionId, r])));
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [versions, plan, itemCode, onSplit]);

  // The earliest unticked version keeps the code; the list is in version order.
  const keeperId = useMemo(() => (versions || []).find((v) => !picked.has(v.id))?.id || null, [versions, picked]);

  const problems = preview ? [...preview.values()].filter((r) => r.problem).length : 0;
  const canSplit = plan.length > 0 && preview && problems === 0 && !previewing && !submitting;

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else if (next.size + 1 < (versions || []).length) next.add(id);   // one has to stay
    return next;
  });

  const handleSplit = async () => {
    setSubmitting(true);
    setError(null);
    const res = await onSplit(itemCode, plan, { dryRun: false });
    setSubmitting(false);
    if (!res.success) { setError(res.error); return; }
    if (!res.ok) {
      setPreview(new Map(res.rows.map((r) => [r.versionId, r])));
      setError('Nothing was split: a version below could not be numbered.');
      return;
    }
    onClose({ newCodes: res.rows.map((r) => r.newCode) });
  };

  const footer = (
    <>
      <span style={{ ...muted, marginRight: 'auto' }}>
        {previewing ? 'Working out the new codes…'
          : plan.length === 0 ? 'Tick the versions to split off.'
          : problems ? `${problems} version${problems === 1 ? '' : 's'} need${problems === 1 ? 's' : ''} attention.`
          : `${plan.length} new item${plan.length === 1 ? '' : 's'}.`}
      </span>
      <BrioButton variant="secondary" onClick={() => onClose()} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSplit} disabled={!canSplit}>
        {submitting ? 'Splitting…' : 'Split'}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title={`Split ${itemCode}`} onClose={() => onClose()} width="xl" footer={footer}>
      <BrioModalBody>
        <p style={{ fontSize: '0.82rem', margin: '0 0 0.6rem' }}>
          Tick the versions to split off. Each becomes its own item and takes its own rates with it. The first
          version you leave unticked keeps {itemCode}; at least one has to stay. Codes follow each version's trade
          type series.
        </p>
        {loadError && <div style={{ color: '#a94442', fontSize: '0.85rem' }}>{loadError}</div>}
        {!versions && !loadError && <div style={muted}>Loading versions…</div>}
        {versions && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', ...muted }}>
                <th style={cell} />
                <th style={cell}>Version</th>
                <th style={cell}>Description</th>
                <th style={cell}>Unit</th>
                <th style={{ ...cell, textAlign: 'right' }}>Rates</th>
                <th style={cell}>Trade type</th>
                <th style={cell}>New code</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => {
                const on = picked.has(v.id);
                const opts = (v.subtradeId && typesByAnchor.get(v.subtradeId)) || [];
                const typeId = typeFor[v.id] || v.tradeTypeId || '';
                const p = preview?.get(v.id);
                const keeps = v.id === keeperId;
                const lastLeft = !on && picked.size + 1 >= versions.length;
                return (
                  <tr key={v.id} style={{ opacity: keeps || on ? 1 : 0.55 }}>
                    <td style={cell}>
                      <input type="checkbox" checked={on} onChange={() => toggle(v.id)} disabled={lastLeft}
                        title={lastLeft ? 'Leave at least one version on the item' : undefined}
                        aria-label={`Split ${v.fullCode}`} />
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      <code>{v.fullCode}</code>
                      {v.status !== 'active' && <div style={muted}>{v.status}</div>}
                    </td>
                    <td style={cell}>{v.shortDescription}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{canonicalUnit(v.unit)}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{v.rateCount}</td>
                    <td style={cell}>
                      {!on ? <span style={muted}>—</span>
                        : opts.length === 0 ? <span style={muted}>{v.subtradeLabel ? 'none under this sub-trade' : 'no sub-trade'}</span>
                        : (
                          <BrioSelect value={typeId}
                            onChange={(e) => setTypeFor((prev) => ({ ...prev, [v.id]: e.target.value }))}
                            style={{ minWidth: 170 }}>
                            <option value="">Choose…</option>
                            {opts.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.label}{t.seriesStart != null ? ` · ${formatSeriesRange(t.seriesStart, t.seriesEnd)}` : ''}
                              </option>
                            ))}
                          </BrioSelect>
                        )}
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {keeps ? <span style={{ fontWeight: 600 }}>keeps {itemCode}</span>
                        : !on ? <span style={muted}>stays</span>
                        : p?.problem ? <span style={{ color: '#a94442' }}>{p.problem}</span>
                        : p?.newCode ? <code style={{ fontWeight: 600 }}>{p.newCode}</code>
                        : <span style={muted}>…</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {error && <div style={{ color: '#a94442', fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</div>}
      </BrioModalBody>
    </BrioModal>
  );
}

export default SplitItemModal;
